import type { TaskScope } from '@hearth/shared';
import { useSyncExternalStore } from 'react';
import { useAuth } from '../../hooks/useAuth';

/** Admins' and managers' Mine / Everyone choice on the Tasks page; the sidebar badge counts the same tasks. */
const STORAGE_KEY = 'hearth.tasks.scope';

function savedScope(): TaskScope | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'mine' || value === 'all' ? value : null;
  } catch {
    return null; // Storage disabled (privacy mode): the choice lasts until the page reloads.
  }
}

let chosen: TaskScope | null = savedScope();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setTaskScope(scope: TaskScope): void {
  try {
    localStorage.setItem(STORAGE_KEY, scope);
  } catch {
    // Not remembered, but still applied to this page.
  }
  chosen = scope;
  for (const listener of listeners) listener();
}

/**
 * Admins and managers oversee the team, so they start on everyone's tasks until they pick "Mine"; everyone else
 * only ever sees their own (`canSeeAll` false, `scope` always 'mine').
 */
export function useTaskScope(): { scope: TaskScope; canSeeAll: boolean; setScope: (scope: TaskScope) => void } {
  const { user } = useAuth();
  const remembered = useSyncExternalStore(subscribe, () => chosen);
  const canSeeAll = user?.role === 'admin' || user?.role === 'manager';
  return { scope: canSeeAll ? (remembered ?? 'all') : 'mine', canSeeAll, setScope: setTaskScope };
}
